import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import type {
  CommissionWithRelations,
  CreateCommissionInput,
  UpdateCommissionInput,
} from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { SearchableSelect } from '../common/SearchableSelect';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { useEvents } from '../../hooks/useEvents';
import type { Event as EventType } from '@peles/shared';

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (data: CreateCommissionInput | UpdateCommissionInput) => Promise<void>;
  defaultVendorId?: string;
  defaultEventId?: string;
  initialValues?: CommissionWithRelations;
  isLoading?: boolean;
  onDelete?: () => void;
}

function toDateInputValue(d: Date | string | null | undefined): string {
  if (!d) return '';
  const dt = new Date(d);
  const year = dt.getUTCFullYear();
  const month = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const day = String(dt.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function CommissionModal({
  open,
  onClose,
  onSave,
  defaultVendorId,
  defaultEventId,
  initialValues,
  isLoading,
  onDelete,
}: Props) {
  const { data: events = [] } = useEvents();
  const isEditing = !!initialValues;

  const [vendorId, setVendorId] = useState(initialValues?.vendorId ?? defaultVendorId ?? '');
  const [eventId, setEventId] = useState<string | null>(
    initialValues?.eventId ?? defaultEventId ?? null,
  );
  const [date, setDate] = useState(
    toDateInputValue(initialValues?.date) || toDateInputValue(new Date()),
  );
  const [amount, setAmount] = useState(String(initialValues?.amount ?? ''));
  const [paidAmount, setPaidAmount] = useState(String(initialValues?.paidAmount ?? '0'));
  const [paidDate, setPaidDate] = useState(toDateInputValue(initialValues?.paidDate));
  const [notes, setNotes] = useState(initialValues?.notes ?? '');
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!vendorId) {
      setError('Vendor is required');
      return;
    }
    if (!date) {
      setError('Date is required');
      return;
    }
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum < 0) {
      setError('Amount must be a valid number');
      return;
    }
    const paidAmountNum = parseFloat(paidAmount || '0');

    const payload = {
      ...(isEditing ? {} : { vendorId }),
      eventId: eventId ?? null,
      date,
      amount: amountNum,
      paidAmount: paidAmountNum,
      paidDate: paidDate || null,
      notes: notes || null,
    };

    try {
      await onSave(payload as CreateCommissionInput | UpdateCommissionInput);
    } catch {
      setError('Failed to save commission');
    }
  }

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title={isEditing ? 'Edit Commission' : 'Add Commission'}
      size="md"
      footer={
        <div className="flex items-center justify-between gap-3">
          <div>
            {isEditing && onDelete && (
              <button
                type="button"
                onClick={onDelete}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md"
              >
                <Trash2 size={14} />
                Delete
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              form="commission-form"
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {isLoading ? 'Saving…' : isEditing ? 'Save Changes' : 'Add Commission'}
            </button>
          </div>
        </div>
      }
    >
      <form id="commission-form" onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
            {error}
          </div>
        )}

        {/* Vendor */}
        {!isEditing && !defaultVendorId && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vendor <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              entityType="vendor"
              value={vendorId}
              onChange={(v) => setVendorId(typeof v === 'string' ? v : (v[0] ?? ''))}
              placeholder="Search vendor..."
            />
          </div>
        )}

        {/* Event (optional) */}
        {!defaultEventId && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Event <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <SearchableListSelect<EventType>
              items={events}
              value={eventId}
              onChange={(v) =>
                setEventId(typeof v === 'string' ? v || null : ((v as string[])[0] ?? null))
              }
              getValue={(e) => e.id}
              getLabel={(e) => e.name}
              placeholder="Select event..."
              clearable
            />
          </div>
        )}

        {/* Date + Amount */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Commission Amount <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              placeholder="0.00"
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Paid amount + Paid date */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount Paid</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={paidAmount}
              onChange={(e) => setPaidAmount(e.target.value)}
              placeholder="0.00"
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Paid Date <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="date"
              value={paidDate}
              onChange={(e) => setPaidDate(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Optional notes..."
            className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        </div>
      </form>
    </EntityModal>
  );
}
